/**
 * DesignerNotesCard — free-text annotation for the mobile Design brief.
 *
 * A shared note explaining choices made during the build (e.g. a
 * substitution). Editable by anyone, no ownership/attribution. Auto-saves
 * on blur via PUT /api/orders/{id}. The textarea grows to fit its content
 * instead of scrolling internally.
 */
import React, { useEffect, useRef } from 'react';
import { useDesignerNote } from '@/hooks/useDesignerNote';
import DesignerNoteStatus from '@/components/design/DesignerNoteStatus';

export function DesignerNotesCard({ order, onNoteSaved }) {
  const note = useDesignerNote(order, { successToast: true, onNoteSaved });
  const { value, setValue, save, readOnly, visible } = note;
  const textareaRef = useRef(null);

  // Auto-grow to fit content instead of scrolling internally.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  if (!visible) return null;

  return (
    <div className="border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="designer-notes-card">
      <div className="flex items-center justify-between px-5 py-2.5 bg-sky-100/60">
        <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">
          Designer Notes
        </span>
      </div>
      <div className="px-5 py-4">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          readOnly={readOnly}
          aria-label="Designer notes"
          aria-busy={note.saving || note.checking}
          placeholder="Add a note about this order…"
          rows={3}
          className="w-full text-sm text-stone-700 leading-relaxed resize-none overflow-hidden border-0 focus:outline-none focus:ring-1 focus:ring-primary/30 rounded p-0 placeholder:text-stone-300 bg-transparent"
          data-testid="designer-notes-textarea"
        />
        <div className="mt-2"><DesignerNoteStatus note={note} testId="designer-notes" /></div>
      </div>
    </div>
  );
}

export default DesignerNotesCard;
